import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { GlobalSearch } from '@monrdv/ui';
import { useTranslation } from 'react-i18next';
import { api, request } from '../lib/api.js';
import { pathInTenant } from '../lib/tenant.js';
import { useRealtime, type ConnectionState } from '../lib/realtime.js';
import { useSession } from '../lib/session.js';
import { Softphone } from './Softphone.js';
import { NotificationTray } from './NotificationTray.js';
import { PeekPanel } from './PeekPanel.js';

/**
 * The application frame.
 *
 * Three things live here because they must be reachable from every screen: the global
 * search, the tenant switcher (one person administers all three businesses), and the phone
 * — a coordinator is on a call while doing everything else, so the softphone cannot be a
 * page you navigate to.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const connection = useRealtime(session.data?.userId ?? null);

  const search = useRouterState({ select: (s) => (s.location.search as { search?: string }).search });
  const query = search ?? '';
  const isSearchOpen = search !== undefined;

  // The query lives in the URL, so a reload reopens the search with the same text and the
  // browser's back button closes it. Replacing rather than pushing keeps one history entry
  // per search session instead of one per keystroke.
  const setQuery = useCallback(
    (next: string) => {
      void navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, search: next }), replace: true });
    },
    [navigate],
  );

  const setSearchOpen = useCallback(
    (open: boolean) => {
      void navigate({
        to: '.',
        // `prev.search` is unknown at this boundary, so the value is narrowed rather than
        // spread through as-is — the router's search type expects a string here.
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          search: open ? ((prev.search as string | undefined) ?? '') : undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const results = useQuery({
    queryKey: ['search', query],
    queryFn: ({ signal }) => api.search({ q: query, grouped: true, limit: 12 }, signal),
    enabled: isSearchOpen && query.trim().length > 0,
    // Results are only worth a moment: someone else may have just renamed the customer.
    staleTime: 5_000,
    placeholderData: (prev) => prev,
  });

  /*
   * Sign-in renders bare: somebody who is not signed in must not be shown navigation for a
   * workspace they cannot reach. Placed after the hooks, never before — an early return above
   * them would change the hook order between renders.
   */
  if (typeof window !== 'undefined' && window.location.pathname === '/connexion') {
    return <>{children}</>;
  }

  return (
    <div className="shell">
      <a href="#main" className="skip-link">
        Aller au contenu
      </a>

      <header className="topbar">
        <TenantBadge
          name={session.data?.tenantName ?? '—'}
          color={session.data?.brandColor ?? 'var(--accent)'}
          tenants={session.data?.tenants ?? []}
          currentId={session.data?.tenantId}
        />

        <div className="topbar__search">
          <GlobalSearch
            query={query}
            onQueryChange={setQuery}
            results={results.data}
            isLoading={results.isFetching}
            open={isSearchOpen}
            onOpenChange={setSearchOpen}
            onNavigate={(path) => navigate({ to: path })}
            onSearchNearby={(position) =>
              navigate({ to: '/jobs', search: { view: 'list', nearLat: position.latitude, nearLng: position.longitude } as never })
            }
            locale={i18n.language === 'en' ? 'en' : 'fr'}
            t={t as (k: string, v?: Record<string, string | number>) => string}
          />
        </div>

        <div className="topbar__right">
          <ConnectionLamp state={connection} />
          <NotificationTray />
          <Softphone />
          <button type="button" className="avatar" aria-label={session.data?.email ?? ''}>
            {initials(session.data?.name ?? '?')}
          </button>
        </div>
      </header>

      <nav className="sidebar" aria-label="Navigation principale">
        <NavGroup>
          <NavLink to="/" label={t('nav.today')} glyph="today" />
          <NavLink to="/board" label={t('nav.board')} glyph="board" />
          <NavLink to="/jobs" label={t('nav.jobs')} glyph="job" />
          <NavLink to="/inbox" label={t('nav.inbox')} glyph="inbox" />
          <NavLink to="/intake" label={t('intake.title')} glyph="intake" />
        </NavGroup>
        <NavGroup label="Clients">
          <NavLink to="/customers" label={t('nav.customers')} glyph="customer" />
          <NavLink to="/quotes" label={t('nav.quotes')} glyph="quote" />
        </NavGroup>
        <NavGroup label="Réglages">
          <NavLink to="/team" label={t('nav.team')} glyph="team" />
          <NavLink to="/journal" label={t('nav.journal')} glyph="journal" />
          <NavLink to="/transcripts" label={t('transcripts.nav')} glyph="transcripts" />
          {/* Where the terms, the internal rules and the Law 25 notice are written and traced. */}
          <NavLink to="/notices" label="Avis" glyph="journal" />
        </NavGroup>
        <NavGroup label="Comptabilité">
          <NavLink to="/invoices" label={t('nav.invoices')} glyph="invoice" />
          <NavLink to="/unbilled" label="À facturer" glyph="unbilled" />
        </NavGroup>
      </nav>

      <main id="main" className="main">
        {children}
      </main>

      {/* Renders only when `?peek=` is present. Deliberately a sibling of <main> rather than
          a child, so it is not affected by page-level layout or scrolling. */}
      <PeekPanel />
    </div>
  );
}

/**
 * The tenant the user is acting in, and the control for changing it.
 *
 * Deliberately prominent and colour-coded. One person administers three separate businesses,
 * and creating an invoice in the wrong one is an expensive mistake — so which business you are
 * in is the first thing on screen, not a line in a dropdown.
 *
 * Switching reloads the page rather than navigating. Every cached query in memory belongs to
 * the business being left; keeping any of it would mean showing one tenant's customers under
 * another's name, which is precisely the mistake the colour-coding exists to prevent.
 */
function TenantBadge({
  name,
  color,
  tenants,
  currentId,
}: {
  name: string;
  color: string;
  tenants: Array<{ id: string; slug: string; name: string; brandColor: string }>;
  currentId: string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on an outside click or on Escape: a menu that can only be dismissed by choosing
  // something is a trap, and this one changes which business you are working in.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const only = tenants.length <= 1;

  async function choose(id: string, slug: string) {
    if (id === currentId) return setOpen(false);
    setBusy(true);
    try {
      /*
       * The session's default is updated too, so the next bare visit to `app.monrdv.ca` lands
       * where this person last was — but it is the URL that decides what this tab shows, and
       * the URL is what changes here. Another tab left open on a different business stays on
       * that business, which is the whole reason the slug moved into the path.
       */
      await request('/auth/tenant', { method: 'POST', body: JSON.stringify({ tenantId: id }) });
      window.location.assign(pathInTenant(slug));
    } catch {
      setBusy(false);
      setOpen(false);
    }
  }

  return (
    <div className="tenant__wrap" ref={ref}>
      <button
        type="button"
        className="tenant"
        style={{ ['--tenant' as string]: color }}
        onClick={() => !only && setOpen((v) => !v)}
        aria-haspopup={only ? undefined : 'menu'}
        aria-expanded={only ? undefined : open}
        // With one workspace there is nothing to switch to, so the control says so rather
        // than opening an empty menu.
        aria-label={only ? name : `${name} — changer d’entreprise`}
        disabled={busy}
      >
        <span className="tenant__mark" aria-hidden="true" />
        <span className="tenant__name">{name}</span>
        {!only && (
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path d="M2 4l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.4" />
          </svg>
        )}
      </button>

      {open && !only && (
        <ul className="tenantMenu" role="menu">
          {tenants.map((t) => (
            <li key={t.id} role="none">
              <button
                type="button"
                role="menuitemradio"
                aria-checked={t.id === currentId}
                className={`tenantMenu__item${t.id === currentId ? ' tenantMenu__item--on' : ''}`}
                onClick={() => void choose(t.id, t.slug)}
                disabled={busy}
              >
                <span className="tenantMenu__mark" style={{ background: t.brandColor }} aria-hidden="true" />
                {t.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Connection state.
 *
 * Shown because live updates change what you can trust on screen. When the socket is down
 * the data is merely stale, not wrong — but a coordinator dispatching work deserves to know
 * which of those they are looking at.
 */
function ConnectionLamp({ state }: { state: ConnectionState }) {
  const label: Record<ConnectionState, string> = {
    connecting: 'Connexion…',
    open: 'À jour',
    reconnecting: 'Reconnexion…',
    offline: 'Hors ligne',
  };
  return (
    <span className={`lamp lamp--${state}`} title={label[state]}>
      <span className="lamp__dot" aria-hidden="true" />
      <span className="lamp__text">{label[state]}</span>
    </span>
  );
}

function NavGroup({ label, children }: { label?: string; children: React.ReactNode }) {
  return (
    <div className="navgroup">
      {label && <span className="label navgroup__label">{label}</span>}
      {children}
    </div>
  );
}

function NavLink({ to, label, glyph }: { to: string; label: string; glyph: string }) {
  return (
    <Link to={to} className="navlink" activeProps={{ className: 'navlink navlink--active' }}>
      <span className="navlink__glyph" aria-hidden="true">
        <NavGlyph name={glyph} />
      </span>
      <span>{label}</span>
    </Link>
  );
}

function NavGlyph({ name }: { name: string }) {
  const p = { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 } as const;
  switch (name) {
    case 'today':
      return (
        <svg {...p}>
          <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
          <path d="M2.5 6.5h11M5.5 2v2.5M10.5 2v2.5" />
        </svg>
      );
    case 'intake':
      // A handset with an inbound arrow: the queue is calls that came to us.
      return (
        <svg {...p}>
          <path d="M3.2 3.5h2.4l1.2 2.8-1.5 1a7.5 7.5 0 0 0 3.4 3.4l1-1.5 2.8 1.2v2.4a1 1 0 0 1-1.1 1A10.5 10.5 0 0 1 2.2 4.6a1 1 0 0 1 1-1.1z" />
          <path d="M13.5 2.5v3h-3" />
        </svg>
      );
    case 'transcripts':
      // Two waveforms, one above the other: the same sound heard two ways.
      return (
        <svg {...p}>
          <path d="M2 5.5h1.5M5 3.5v4M7.5 4.5v2M10 3v5M12.5 5h1.5" />
          <path d="M2 11h1.5M5 9.5v3M7.5 10.5v1M10 9v4M12.5 10.5h1.5" opacity="0.5" />
        </svg>
      );
    case 'journal':
      return (
        <svg {...p}>
          <path d="M3.5 2.5h9v11h-9z" />
          <path d="M6 5.5h4M6 8h4M6 10.5h2.5" />
        </svg>
      );
    case 'team':
      return (
        <svg {...p}>
          <circle cx="6" cy="5.5" r="2.2" />
          <path d="M2 13.5c0-2.2 1.8-3.5 4-3.5s4 1.3 4 3.5" />
          <path d="M11 4.2a2.2 2.2 0 0 1 0 4.3M12.2 13.5c0-1.6-.6-2.6-1.6-3.2" />
        </svg>
      );
    case 'board':
      // A van, because the screen is about people who are out in one.
      return (
        <svg {...p}>
          <path d="M1.5 10.5V5h7.5v5.5M9 7h2.5l2 2.5v1H9" />
          <circle cx="4.5" cy="11.5" r="1.3" />
          <circle cx="11" cy="11.5" r="1.3" />
        </svg>
      );
    case 'job':
      // The duct elbow again: the same shape the search results use for work.
      return (
        <svg {...p}>
          <path d="M2 5h6a3 3 0 0 1 3 3v6" />
        </svg>
      );
    case 'inbox':
      return (
        <svg {...p}>
          <path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" />
        </svg>
      );
    case 'customer':
      return (
        <svg {...p}>
          <circle cx="8" cy="5.5" r="2.5" />
          <path d="M2.5 14c0-2.8 2.5-4.5 5.5-4.5s5.5 1.7 5.5 4.5" />
        </svg>
      );
    case 'quote':
      return (
        <svg {...p}>
          <path d="M4 2h6l3 3v9H4z" />
        </svg>
      );
    case 'invoice':
      return (
        <svg {...p}>
          <path d="M4 2h8v12l-2-1.3-2 1.3-2-1.3L4 14z" />
        </svg>
      );
    case 'unbilled':
      // An invoice outline left open: work recorded, bill never closed.
      return (
        <svg {...p}>
          <path d="M4 2h8v12l-2-1.3-2 1.3-2-1.3L4 14z" strokeDasharray="2.5 2" />
          <path d="M8 5.5v3M8 10.5v.01" />
        </svg>
      );
    default:
      return <svg {...p} />;
  }
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
